import { CaseScopeCode } from '@gql'
import type { MessageValue, NoMessageValues } from 'react-intl'
import { defineMessages } from 'react-intl'

/** Customer-identity copy: how an import linked a lead, and what blocks a case on it. */
export const caseIdentityMessages = defineMessages<{
  readonly blockerCasePending: NoMessageValues
  readonly blockerSignedDocuments: NoMessageValues
  readonly blockerTagAbsent: NoMessageValues
  readonly cardBlocked: NoMessageValues
  readonly cardCustomerName: { readonly name: MessageValue }
  readonly cardMergedName: { readonly name: MessageValue }
  readonly cardSubmittedName: { readonly name: MessageValue }
  readonly cardText: NoMessageValues
  readonly cardTitle: NoMessageValues
  readonly classificationSuspectedDuplicate: NoMessageValues
  readonly confirmAction: NoMessageValues
  readonly confirmDialogText: { readonly name: MessageValue }
  readonly confirmDialogTitle: NoMessageValues
  readonly confirmSuccess: NoMessageValues
  readonly differentDebtorAction: NoMessageValues
  readonly documentStaleAfterMove: NoMessageValues
  readonly documentsBlocked: NoMessageValues
  readonly documentsBlockedLink: NoMessageValues
  readonly documentsBlockedReason: NoMessageValues
  readonly eventConfirmed: { readonly customer: MessageValue }
  readonly eventCustomerMerged: { readonly from: MessageValue; readonly to: MessageValue }
  readonly eventCustomerMergedNoSource: { readonly to: MessageValue }
  readonly eventMoved: { readonly from: MessageValue; readonly to: MessageValue }
  readonly eventUnknownCustomer: NoMessageValues
  readonly ktpAcknowledged: { readonly name: MessageValue }
  readonly ktpDifferentDebtor: NoMessageValues
  readonly ktpIdentityCheckNoEdit: NoMessageValues
  readonly ktpIdentityCheckText: { readonly name: MessageValue }
  readonly ktpMismatchText: {
    readonly name: MessageValue
    readonly onFile: MessageValue
    readonly scanned: MessageValue
  }
  readonly ktpSamePerson: NoMessageValues
  readonly ktpTitle: NoMessageValues
  readonly mergedTwinClosed: NoMessageValues
  readonly mergedTwinText: { readonly caseType: MessageValue }
  readonly mergedTwinTitle: NoMessageValues
  readonly moveConfirm: { readonly code: MessageValue }
  readonly moveDialogText: { readonly code: MessageValue; readonly name: MessageValue }
  readonly moveDialogTitle: NoMessageValues
  readonly moveName: NoMessageValues
  readonly moveNik: NoMessageValues
  readonly moveNote: NoMessageValues
  readonly movePhoneKeep: { readonly name: MessageValue }
  readonly movePhoneKeepInfo: { readonly name: MessageValue }
  readonly movePhoneKeepInfoNoThread: { readonly name: MessageValue }
  readonly movePhoneLabel: NoMessageValues
  readonly movePhoneMove: { readonly phoneNumber: MessageValue }
  readonly movePhoneMoveInfo: NoMessageValues
  readonly movePhoneMoveNoPhone: NoMessageValues
  readonly movePhoneMoveVerified: { readonly phoneNumber: MessageValue }
  readonly movePhoneNumber: NoMessageValues
  readonly movePhoneOwn: NoMessageValues
  readonly movePhoneOwnInfo: { readonly name: MessageValue }
  readonly moveSuccess: { readonly name: MessageValue }
  readonly noteLabel: NoMessageValues
  readonly notePlaceholder: NoMessageValues
  readonly reasoningSharedNumber: {
    readonly clusterSize: MessageValue
    readonly maxCluster: MessageValue
  }
  readonly sharedNumberOpenCase: NoMessageValues
  readonly sharedNumberText: { readonly code: MessageValue; readonly customer: MessageValue }
  readonly sharedNumberTextNoCase: NoMessageValues
  readonly threadHeldByOtherCustomer: NoMessageValues
}>({
  blockerCasePending: {
    defaultMessage: 'A pending case cannot move to another customer. Open it first.',
    description: 'case identity check — why "Different debtor" is disabled',
  },
  blockerSignedDocuments: {
    defaultMessage:
      'This case has documents sent for e-signature or signed, so it cannot move to another customer.',
    description: 'case identity check — why "Different debtor" is disabled',
  },
  blockerTagAbsent: {
    defaultMessage: 'This case has no identity check to settle.',
    description: 'case identity check — why "Different debtor" is disabled',
  },
  cardBlocked: {
    defaultMessage: "Contract and e-sign documents can't be generated until this is settled.",
    description: 'case identity check card — what the check blocks',
  },
  cardCustomerName: {
    defaultMessage: 'Customer on this number: {name}',
    description: 'case identity check card — the customer the lead linked to',
  },
  cardMergedName: {
    defaultMessage: 'Name on a customer merged into this one: {name}',
    description:
      'case identity check card — the conflicting name of a customer merged into this one',
  },
  cardSubmittedName: {
    defaultMessage: 'Name on the lead: {name}',
    description: 'case identity check card — the name the lead came in with',
  },
  cardText: {
    defaultMessage:
      'This lead came in with a different name than the customer on this phone number. Settle who the debtor is.',
    description: 'case identity check card — body',
  },
  cardTitle: {
    defaultMessage: 'Check who this lead is',
    description: 'case identity check card — title',
  },
  classificationSuspectedDuplicate: {
    defaultMessage: 'Possible duplicate (old review queue)',
    description:
      'case routing identity classification — rows filed before the duplicate review queue was removed',
  },
  confirmAction: {
    defaultMessage: 'Belongs to this customer',
    description: 'case identity check — button: the lead is this customer, or acts for them',
  },
  confirmDialogText: {
    defaultMessage:
      'The case stays with {name}. Use this when the lead is the same person or someone acting for them, such as a spouse handling the debt. The identity check is cleared.',
    description: 'case identity confirm dialog text',
  },
  confirmDialogTitle: {
    defaultMessage: 'Belongs to this customer',
    description: 'dialog header',
  },
  confirmSuccess: {
    defaultMessage: 'Identity check settled. The case stays with this customer.',
    description: 'case identity confirm success notification',
  },
  differentDebtorAction: {
    defaultMessage: 'Different debtor',
    description: 'case identity check — button: the lead is another person; move the case',
  },
  documentStaleAfterMove: {
    defaultMessage:
      'This document was generated before the case moved to a new customer. Generate it again, then request the signature.',
    description: 'request e-signature refused — the document predates the case move',
  },
  documentsBlocked: {
    defaultMessage:
      'Contract and e-sign documents are blocked: this case may belong to a different person.',
    description: 'case documents tab — identity check blocks document generation',
  },
  documentsBlockedLink: {
    defaultMessage: 'Settle it on the Overview tab',
    description: 'case documents tab — link to the identity check card',
  },
  documentsBlockedReason: {
    defaultMessage: 'Blocked until the identity check on this case is settled.',
    description: 'case documents — tooltip on the disabled Generate and Request signature actions',
  },
  eventConfirmed: {
    defaultMessage: 'Confirmed as {customer}.',
    description: 'case history message — identity check settled: the case belongs to this customer',
  },
  eventCustomerMerged: {
    defaultMessage: 'Customer {from} was merged into {to}.',
    description: 'case history message — the case followed a customer merge',
  },
  eventCustomerMergedNoSource: {
    defaultMessage: 'Another customer was merged into {to}; this case came with it.',
    description:
      'case history message — the case followed a customer merge; the merged-away customer no longer exists',
  },
  eventMoved: {
    defaultMessage: 'Moved from {from} to {to}.',
    description: 'case history message — the case moved to a new customer',
  },
  eventUnknownCustomer: {
    defaultMessage: 'a removed customer',
    description: 'case history message — placeholder for a customer that no longer exists',
  },
  ktpAcknowledged: {
    defaultMessage: 'Same person. Confirm saves this KTP to {name}.',
    description: 'KTP scan check — the operator said the KTP belongs to the customer',
  },
  ktpDifferentDebtor: {
    defaultMessage: 'Different debtor — move this case',
    description: 'KTP scan check — button: the KTP is another person; move the case',
  },
  ktpIdentityCheckNoEdit: {
    defaultMessage:
      'Someone who can edit this case must settle its identity check before this KTP is saved.',
    description: 'KTP scan check on an identity-check case — the viewer cannot settle it',
  },
  ktpIdentityCheckText: {
    defaultMessage:
      'This case has an open identity check. Say whether this KTP belongs to {name} before saving it.',
    description: 'KTP scan check — the case carries the identity-check tag',
  },
  ktpMismatchText: {
    defaultMessage: "This KTP's NIK ({scanned}) is different from the NIK on {name} ({onFile}).",
    description: 'KTP scan check — the scanned NIK differs from the one on file',
  },
  ktpSamePerson: {
    defaultMessage: 'Same person',
    description: 'KTP scan check — button: the KTP belongs to the customer',
  },
  ktpTitle: {
    defaultMessage: 'Check who this KTP belongs to',
    description: 'KTP scan check — title',
  },
  mergedTwinClosed: {
    defaultMessage: 'The other case is closed. Remove this tag.',
    description: 'merged open twin notice — no other open case remains',
  },
  mergedTwinText: {
    defaultMessage:
      'These customer records were merged. Another open {caseType} case exists. Close the one that is no longer needed.',
    description: 'merged open twin notice — body',
  },
  mergedTwinTitle: {
    defaultMessage: 'Merged customer: another open case',
    description: 'merged open twin notice — title',
  },
  moveConfirm: {
    defaultMessage: 'Move case {code}',
    description: 'case move to new customer dialog — confirm button',
  },
  moveDialogText: {
    defaultMessage:
      'A new customer is created and case {code} moves to them from {name}. This cannot be undone here.',
    description: 'case move to new customer dialog text',
  },
  moveDialogTitle: {
    defaultMessage: 'Move to a new customer',
    description: 'dialog header',
  },
  moveName: {
    defaultMessage: "New customer's name",
    description: 'case move to new customer dialog — name field',
  },
  moveNik: {
    defaultMessage: 'NIK (from the scanned KTP)',
    description: 'case move to new customer dialog — read-only NIK row',
  },
  moveNote: {
    defaultMessage: 'Note (optional)',
    description: 'case move to new customer dialog — note field',
  },
  movePhoneKeep: {
    defaultMessage: 'The number stays with {name}',
    description: 'case move phone option — the new customer gets no phone',
  },
  movePhoneKeepInfo: {
    defaultMessage:
      'The WhatsApp conversation stays with {name}. This case will link to it as a shared number.',
    description: 'case move phone option — what happens to WhatsApp when the number stays',
  },
  movePhoneKeepInfoNoThread: {
    defaultMessage:
      '{name} keeps the number. There is no WhatsApp conversation on it for this case to link to.',
    description:
      'case move phone option — the number stays, and it has no WhatsApp conversation this case could link to',
  },
  movePhoneLabel: {
    defaultMessage: 'Phone number',
    description: 'case move to new customer dialog — phone options label',
  },
  movePhoneMove: {
    defaultMessage: 'The number is theirs: {phoneNumber} moves to the new customer',
    description: 'case move phone option — the case phone moves',
  },
  movePhoneMoveInfo: {
    defaultMessage: 'The WhatsApp conversation on this number moves to the new customer.',
    description: 'case move phone option — what happens to WhatsApp when the number moves',
  },
  movePhoneMoveNoPhone: {
    defaultMessage: 'The number is theirs (the case has no phone to move)',
    description: 'case move phone option, disabled — no case phone',
  },
  movePhoneMoveVerified: {
    defaultMessage:
      'The number is theirs ({phoneNumber} is verified for the portal and cannot move)',
    description: 'case move phone option, disabled — the case phone is verified',
  },
  movePhoneNumber: {
    defaultMessage: 'Their phone number',
    description: 'case move to new customer dialog — own number field',
  },
  movePhoneOwn: {
    defaultMessage: 'They have their own number',
    description: 'case move phone option — the new customer gets their own number',
  },
  movePhoneOwnInfo: {
    defaultMessage: 'The WhatsApp conversation on this number stays with {name}.',
    description: 'case move phone option — what happens to WhatsApp with an own number',
  },
  moveSuccess: {
    defaultMessage: 'Case moved to {name}.',
    description: 'case move to new customer success notification',
  },
  noteLabel: {
    defaultMessage: 'Note (optional)',
    description: 'case identity confirm dialog — note field',
  },
  notePlaceholder: {
    defaultMessage: 'e.g. chatting: wife',
    description: 'case identity confirm dialog — note placeholder',
  },
  reasoningSharedNumber: {
    defaultMessage:
      'Shared number: {clusterSize} customers hold this phone (limit {maxCluster}), so no lead on it is closed automatically.',
    description:
      'case routing identity reasoning — the phone is held by more customers than the cluster limit; the lead still linked',
  },
  sharedNumberOpenCase: {
    defaultMessage: 'Open that case',
    description: 'case WhatsApp card, shared number — open the number holder’s case',
  },
  sharedNumberText: {
    defaultMessage:
      'Shared number: this number’s WhatsApp conversation is with {customer} on case #{code}.',
    description: 'case WhatsApp card — the case moved off a number another customer keeps',
  },
  sharedNumberTextNoCase: {
    defaultMessage: 'Shared number: this number’s WhatsApp conversation is with another customer.',
    description:
      'case WhatsApp card — the case moved off a shared number; the holder’s case is not visible',
  },
  threadHeldByOtherCustomer: {
    defaultMessage:
      "WhatsApp is blocked for this case: the customer's number already has a conversation that belongs to a different customer record. To message from here, ask a manager to merge the two customer records, or archive that conversation.",
    description: 'case routing thread-held-by-other-customer warning',
  },
})

/** Why "Different debtor" is disabled, for the codes `Case.moveToNewCustomerBlocker` returns. */
export const caseMoveBlockerMessage = (code: CaseScopeCode | null | undefined) => {
  switch (code) {
    case CaseScopeCode.MOVE_CASE_PENDING:
      return caseIdentityMessages.blockerCasePending
    case CaseScopeCode.MOVE_SIGNED_DOCUMENTS:
      return caseIdentityMessages.blockerSignedDocuments
    case CaseScopeCode.MOVE_TAG_ABSENT:
      return caseIdentityMessages.blockerTagAbsent
    default:
      return null
  }
}
